import api from './api';

/**
 * Interview Service - API client for interview management
 */
const interviewService = {
  /**
   * Schedule a new interview
   * @param {Object} data - Interview data
   * @param {string} data.applicationId - Application ID
   * @param {string} data.scheduledAt - ISO8601 date/time
   * @param {string} data.interviewerId - Interviewer user ID
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Scheduled interview
   */
  scheduleInterview: async (data) => {
    const response = await api.post('/interviews', data);
    return response.data;
  },

  /**
   * Cancel an interview
   * @param {string} interviewId - Interview ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise<Object>} Cancelled interview
   */
  cancelInterview: async (interviewId, reason) => {
    const response = await api.patch(`/interviews/${interviewId}/cancel`, { reason });
    return response.data;
  },

  /**
   * Complete an interview
   * @param {string} interviewId - Interview ID
   * @param {Object} data - Completion data
   * @param {string} data.outcome - PASSED or FAILED
   * @param {string} data.notes - Optional notes
   * @returns {Promise<Object>} Completed interview and updated application
   */
  completeInterview: async (interviewId, data) => {
    const response = await api.patch(`/interviews/${interviewId}/complete`, data);
    return response.data;
  },

  /**
   * Update meeting details for an interview
   * @param {string} interviewId - Interview ID
   * @param {Object} data - Meeting details
   * @param {string} data.meetingLink - Meeting URL
   * @param {string} data.meetingInstructions - Optional instructions
   * @returns {Promise<Object>} Updated interview
   */
  updateMeetingDetails: async (interviewId, data) => {
    const response = await api.patch(`/interviews/${interviewId}/meeting`, data);
    return response.data;
  },

  /**
   * Candidate acknowledges interview availability
   * @param {string} interviewId - Interview ID
   * @param {Object} data - Candidate availability data
   * @param {boolean} data.isAvailable - Whether candidate is available
   * @param {string} data.nextAvailableAt - Required when isAvailable is false
   * @returns {Promise<Object>} Updated interview
   */
  updateCandidateAvailability: async (interviewId, data) => {
    const response = await api.patch(`/interviews/${interviewId}/availability`, data);
    return response.data;
  },

  /**
   * Get calendar view of interviews
   * @param {string} start - Start date (ISO8601)
   * @param {string} end - End date (ISO8601)
   * @returns {Promise<Array>} Calendar data grouped by date
   */
  getCalendar: async (start, end) => {
    const response = await api.get(`/interviews/calendar?start=${start}&end=${end}`);
    return response.data;
  },

  /**
   * Get my interviews (as interviewer or candidate)
   * @returns {Promise<Array>} List of interviews
   */
  getMyInterviews: async () => {
    const response = await api.get('/interviews/my');
    return response.data;
  },

  /**
   * Get interview for specific application
   * @param {string} applicationId - Application ID
   * @returns {Promise<Object|null>} Interview or null
   */
  getApplicationInterview: async (applicationId, eventType = null) => {
    const suffix = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
    const response = await api.get(`/legacy-applications/${applicationId}/interview${suffix}`);
    // Backend returns {success: true, data: interview}, so we need to extract data.data
    return response.data.data;
  }
};

export default interviewService;
